// src/components/Dashboard.jsx
import { OILS, LOW_STOCK_THRESHOLD_KG } from "../constants";
import { fmt, fmtL, fmtKg, today } from "../utils/ui";
import { OilDot, PayPill, MetCard, AlertBar, EmptyState, S } from "../utils/ui";

export default function Dashboard({ state }) {
  const { rawStock, oilPlant, oilShop, cakeStock, prices, sales, cakeSales } = state;

  const todayStr = today();
  const todaySales = sales.filter(s => s.date === todayStr);
  const todayCake  = cakeSales.filter(s => s.date === todayStr);

  const totalRawV  = OILS.reduce((a, o) => a + rawStock[o] * (prices.raw[o] || 0), 0);
  const totalOilV  = OILS.reduce((a, o) => a + (oilPlant[o] + oilShop[o]) * (prices.oil?.[o]?.["1L"] || 0), 0);
  const totalCakeV = OILS.reduce((a, o) => a + cakeStock[o] * (prices.cake[o] || 0), 0);
  const todayRev   = todaySales.reduce((a, s) => a + s.amount, 0);
  const todayCakeRev = todayCake.reduce((a, s) => a + s.amount, 0);
  const totalOilL  = OILS.reduce((a, o) => a + oilPlant[o] + oilShop[o], 0);
  const totalRawKg = OILS.reduce((a, o) => a + rawStock[o], 0);

  const lowStocks = OILS.filter(o => rawStock[o] > 0 && rawStock[o] < LOW_STOCK_THRESHOLD_KG);

  // Recent transactions (oil + cake combined, sorted newest first)
  const allTx = [
    ...sales.map(s => ({ ...s, txType: "oil" })),
    ...cakeSales.map(s => ({ ...s, txType: "cake" })),
  ].sort((a, b) => b.ts - a.ts).slice(0, 10);

  return (
    <div style={{ padding: "12px 14px" }}>
      {/* Low stock alerts */}
      {lowStocks.length > 0 && (
        <AlertBar text={`Low raw material (under 50 kg): ${lowStocks.join(", ")}`} type="warning" />
      )}

      {/* Today's at-a-glance metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <MetCard label="Today's oil sales" value={fmtL(todaySales.reduce((a, s) => a + s.litres, 0))} sub={fmt(todayRev)} accent="#27500A" />
        <MetCard label="Today's cake sales" value={fmtKg(todayCake.reduce((a, s) => a + s.kg, 0))} sub={fmt(todayCakeRev)} accent="#185FA5" />
        <MetCard label="Oil in hand" value={fmtL(totalOilL)} sub={fmt(totalOilV)} accent="#854F0B" />
        <MetCard label="Raw material" value={fmtKg(totalRawKg)} sub={fmt(totalRawV)} accent="#3B6D11" />
      </div>

      {/* Per-oil inventory breakdown */}
      <div style={S.sectionTitle}>Inventory by oil type</div>
      {OILS.map(o => {
        const hasAny = rawStock[o] > 0 || oilPlant[o] > 0 || oilShop[o] > 0 || cakeStock[o] > 0;
        return (
          <div key={o} style={{ ...S.card, opacity: hasAny ? 1 : 0.45 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hasAny ? 8 : 0 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <OilDot oil={o} />
                <strong style={{ fontSize: 14 }}>{o}</strong>
              </div>
              {!hasAny && <span style={{ fontSize: 12, color: "#aaa" }}>No stock yet</span>}
              {rawStock[o] > 0 && rawStock[o] < LOW_STOCK_THRESHOLD_KG && (
                <span style={{ fontSize: 10, background: "#FAECE7", color: "#993C1D", padding: "2px 8px", borderRadius: 8 }}>
                  Low stock
                </span>
              )}
            </div>
            {hasAny && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                <div>
                  <div style={{ color: "#888" }}>Raw material</div>
                  <strong>{fmtKg(rawStock[o])}</strong>
                  <div style={{ color: "#aaa", fontSize: 11 }}>{fmt(rawStock[o] * (prices.raw[o] || 0))}</div>
                </div>
                <div>
                  <div style={{ color: "#888" }}>Oil — plant</div>
                  <strong>{fmtL(oilPlant[o])}</strong>
                  <div style={{ color: "#aaa", fontSize: 11 }}>{fmt(oilPlant[o] * (prices.oil?.[o]?.["1L"] || 0))}</div>
                </div>
                <div>
                  <div style={{ color: "#888" }}>Oil — shop</div>
                  <strong>{fmtL(oilShop[o])}</strong>
                  <div style={{ color: "#aaa", fontSize: 11 }}>{fmt(oilShop[o] * (prices.oil?.[o]?.["1L"] || 0))}</div>
                </div>
                <div>
                  <div style={{ color: "#888" }}>Cake</div>
                  <strong>{fmtKg(cakeStock[o])}</strong>
                  <div style={{ color: "#aaa", fontSize: 11 }}>{fmt(cakeStock[o] * (prices.cake[o] || 0))}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Value summary */}
      <div style={S.card}>
        <div style={S.cardTitle}>Total portfolio value</div>
        {[
          ["Raw material stock",  totalRawV,  "#555",    400],
          ["Oil stock (all)",     totalOilV,  "#555",    400],
          ["Cake stock",          totalCakeV, "#555",    400],
          ["Grand total",         totalRawV + totalOilV + totalCakeV, "#27500A", 700],
        ].map(([label, val, color, weight]) => (
          <div key={label} style={{ ...S.row, fontWeight: weight }}>
            <span style={{ fontSize: 13, color }}>{label}</span>
            <span style={{ fontSize: 13, color }}>{fmt(val)}</span>
          </div>
        ))}
      </div>

      {/* Shop vs plant oil breakdown */}
      <div style={S.card}>
        <div style={S.cardTitle}>Oil location breakdown</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div style={{ background: "#F7F4EE", borderRadius: 8, padding: "10px 12px", borderLeft: "3px solid #27500A" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>At plant</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtL(OILS.reduce((a, o) => a + oilPlant[o], 0))}</div>
          </div>
          <div style={{ background: "#F7F4EE", borderRadius: 8, padding: "10px 12px", borderLeft: "3px solid #185FA5" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>At shop</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtL(OILS.reduce((a, o) => a + oilShop[o], 0))}</div>
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div style={S.card}>
        <div style={S.cardTitle}>Recent transactions</div>
        {allTx.length === 0
          ? <EmptyState text="No transactions yet. Start by logging a purchase!" />
          : allTx.map((s, i, arr) => (
            <div key={s.ts} style={{ ...S.row, ...(i === arr.length - 1 ? { borderBottom: "none" } : {}) }}>
              <div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <OilDot oil={s.oil} />
                  <strong style={{ fontSize: 13 }}>{s.oil}</strong>
                  <span style={{ fontSize: 12, color: "#888", marginLeft: 6 }}>
                    {s.txType === "cake" ? "cake" : s.sku}
                  </span>
                  <PayPill mode={s.mode} />
                </div>
                <div style={{ fontSize: 11, color: "#aaa", paddingLeft: 14, marginTop: 2 }}>
                  {s.location || "Plant"} · {s.by} · {s.date}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(s.amount)}</div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  {s.txType === "cake" ? fmtKg(s.kg) : fmtL(s.litres)}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
